// Final Kabukicho pass: authored roof uses, retained landmark fronts and neon trim.
// Exact IDs keep the treatment out of Okubo and Nishi-Shinjuku.
import type { HdBuilding } from '../../common/src/data';
import type { LightFn } from './hd-registry';
import { type G, K, HT, px, frame, disc, alpha } from './hd-kit';
import { panel, fan, pipe, window, label } from './hd-hachi-study';

type RoofUse = 'plant' | 'duct' | 'glass' | 'deck' | 'screen';
// Placement is in HD pixels relative to the roof; shallow north roofs get compact units.
const ROOFS: Record<string, readonly [RoofUse, number, number]> = {
  'top-club': ['screen', 130, 20], 'top-izakaya': ['duct', 110, 25],
  'top-hotel': ['glass', 204, 9], 'top-snack': ['deck', 35, 20],
  'edge-01': ['duct', 18, 245], 'edge-02': ['glass', 20, 240],
  'edge-03': ['screen', 19, 240], 'edge-04': ['plant', 18, 240],
  'edge-05': ['glass', 20, 240], 'edge-06': ['deck', 19, 240],
  'edge-07': ['duct', 19, 243], 'edge-08': ['screen', 19, 176],
  'a-flank': ['glass', 20, 240], 'clinic': ['plant', 38, 155],
  'a-snack': ['duct', 19, 60], 'a-love': ['glass', 170, 280],
  'b-pachinko': ['screen', 90, 76], 'b-yakitori': ['duct', 90, 101],
  'b-club': ['deck', 70, 55], 'c-snack': ['screen', 93, 107],
  'c-karaoke': ['glass', 164, 107], 'c-ramen': ['duct', 190, 111],
  'd-block-nw': ['glass', 150, 200], 'd-club': ['deck', 150, 165],
  'd-block-ne': ['screen', 155, 120], 'd-yakitori': ['duct', 125, 138],
  'e-arcade': ['glass', 100, 120], 'e-snack': ['screen', 206, 114],
  'n-izakaya': ['duct', 75, 23], 'n-snack': ['screen', 81, 20],
  'n-karaoke': ['glass', 181, 20], 'n-bar': ['deck', 80, 19],
  'n-office': ['screen', 70, 20], 'n-konbini': ['plant', 91, 20],
  'n-hotel': ['screen', 105, 8], 'n-arcade': ['glass', 66, 22],
  'n-pachinko': ['duct', 23, 29], 'n-flats': ['deck', 71, 22],
  'n-shutter': ['screen', 143, 20],
};
export const FINAL_PASS_BUILDINGS = Object.keys(ROOFS);

function roofUnit(g: G, use: RoofUse, x: number, y: number, w: number, h: number) {
  if (use === 'glass') {
    window(g, x, y, w, h, K.glassLo);
    for (let j = 12; j < w - 4; j += 16) px(g, x + j, y + 3, 2, h - 6, K.metalHi);
    px(g, x + 4, y + h - 3, w - 8, 2, K.ink);
  } else if (use === 'deck') {
    panel(g, x, y, w, h, K.woodLo);
    for (let j = 7; j < w - 3; j += 7) px(g, x + j, y + 3, 1, h - 6, K.woodHi);
    for (const xx of [x + 6, x + w - 17]) {
      panel(g, xx, y + h - 13, 11, 9, K.wood);
      px(g, xx + 2, y + h - 15, 7, 4, K.leafLo);
    }
  } else if (use === 'screen') {
    panel(g, x, y, w, h, K.metalLo);
    for (let j = 5; j < h - 3; j += 5) px(g, x + 4, y + j, w - 8, 1, K.metalHi);
    px(g, x + w / 2, y + 2, 2, h - 4, K.ink);
  } else if (use === 'plant' && h >= 31) {
    panel(g, x, y, w, h, K.concreteLo);
    fan(g, x + 4, y + 3);
    if (w >= 70) fan(g, x + 38, y + 3);
    px(g, x + 5, y + h - 4, w - 10, 2, K.metalHi);
  } else {
    // Insulated kitchen duct, with folded seams and dark intake grille.
    panel(g, x, y, w, h, K.metal);
    for (let j = 12; j < w - 6; j += 18) px(g, x + j, y + 3, 2, h - 6, K.metalLo);
    frame(g, x + w - 17, y + 4, 12, h - 8, K.ink);
    for (let j = 7; j < h - 5; j += 4) px(g, x + w - 15, y + j, 8, 1, K.ink);
  }
}

export function paintKabukiFinalPass(g: G, b: HdBuilding, x: number, y: number, L: LightFn) {
  const spec = ROOFS[b.id];
  if (!spec) return;
  const w = b.w * HT, h = b.h * HT, rh = (b.h - b.face) * HT;
  const [use, ux, uy] = spec;
  g.save(); g.beginPath(); g.rect(x, y, w, rh); g.clip();
  // Set-back parapet, bolted coping, perimeter cable tray and drain collars.
  frame(g, x + 8, y + 8, w - 16, rh - 16, K.metalLo);
  px(g, x + 9, y + 9, w - 18, 1, K.metalHi);
  for (let j = 22; j < w - 15; j += 32) px(g, x + j, y + 9, 2, 2, K.ink);
  pipe(g, x + w - 17, y + 15, rh - 28);
  const uw = Math.min(b.w <= 4 ? 76 : 96, w - ux - 22), uh = Math.min(43, rh - uy - 11);
  roofUnit(g, use, x + ux, y + uy, uw, uh);
  if (rh > 140) {
    // Narrow maintenance grating follows the lower roof edge, not the sidewalk.
    panel(g, x + 18, y + rh - 25, w - 40, 12, K.metalLo);
    for (let j = 23; j < w - 26; j += 8) px(g, x + j, y + rh - 22, 2, 6, K.ink);
    disc(g, x + w - 29, y + rh - 35, 6, 4, K.metalHi);
    disc(g, x + w - 29, y + rh - 35, 3, 2, K.ink);
  }
  if (b.id === 'a-love') {
    label(g, 'HOTEL', x + 34, y + 273, 96, K.pink, 2);
    frame(g, x + 32, y + 271, 100, 28, K.pink);
    L(x + 34, y + 273, 96, 24, K.pink, .35, false);
  }
  if (b.id === 'clinic') {
    // Clean screened air-handling plant and a service cable linking the original units.
    panel(g, x + 149, y + 159, 53, 39, K.plasterLo);
    for (let j = 5; j < 34; j += 5) px(g, x + 153, y + 159 + j, 45, 2, K.metalHi);
    px(g, x + 130, y + 177, 19, 5, K.metalLo);
  }
  g.restore();
  if (!b.face || b.kind === 'kwestBack') return;
  // Original doors, sign lettering, robot mascot and clinic cross stay readable.
  const fy = y + rh, bottom = y + h;
  g.save(); g.beginPath(); g.rect(x, fy, w, h - rh); g.clip();
  for (const xx of [x + 2, x + w - 5]) {
    px(g, xx, fy + 5, 3, h - rh - 9, K.metalLo);
    px(g, xx, fy + 5, 1, h - rh - 9, K.metalHi);
  }
  px(g, x + 6, bottom - 3, w - 12, 2, K.ink);
  if (b.id === 'clinic') {
    for (const xx of [x + 6, x + w - 9]) {
      px(g, xx - 2, fy + 8, 7, h - rh - 16, alpha(K.cyan, .14));
      px(g, xx, fy + 8, 2, h - rh - 16, K.cyan);
    }
    L(x + 16, bottom - 12, w - 32, 8, K.cyan, .35, true);
  } else if (b.id === 'n-flats' || b.id === 'n-office' || b.id === 'n-shutter') {
    // Quiet upper-service row: warm wall lamps, no entertainment marquee.
    for (const xx of [x + 8, x + w - 17]) {
      panel(g, xx, bottom - 19, 9, 11, K.metalLo);
      px(g, xx + 2, bottom - 17, 5, 5, K.warmHi);
      L(xx, bottom - 17, 9, 8, K.amber, .2, true);
    }
  }
  g.restore();
}
